// A condition tree in words (spec/dashboard.md section 7.1) and its time status: the same renderer labels the closed box,
// the cards, the grid cells and the Changes panel; the status previews the schedule from the time leaves alone.
import type { Json } from '../../api/types';
import { formatDuration, formatInstant, zoneLabel, type DisplayZone } from '../../ui/time';
import type { ContentCatalog } from '../ContentContext';
import type { DocumentLike } from '../format';
import { CHECK_TYPES, CONDITION_NS, REVENUE_KINDS, comparisonText, daysText, kindOf, minutesOf } from './model';
import { childIds, nodeOf } from './ops';

export interface TextContext {
  zone?: DisplayZone;
  offsets?: { dev?: number; stage?: number };
}

const MAX_DEPTH = 12;

function data(node: DocumentLike | undefined): Record<string, Json> {
  return (node?.data ?? {}) as Record<string, Json>;
}

function pathText(fullPath: Json | undefined): string {
  if (!fullPath || typeof fullPath !== 'object' || Array.isArray(fullPath)) return 'field?';
  const fp = fullPath as { profile?: string; path?: string };
  const profile = fp.profile ? fp.profile.split('.').pop() : '';
  const path = fp.path ?? '';
  return profile && profile !== 'UnnyProfile' ? `${profile} › ${path.replace(/\./g, ' › ')}` : path.replace(/\./g, ' › ') || 'field?';
}

function ref(catalog: ContentCatalog, id: Json | undefined, what: string): string {
  if (typeof id !== 'string' || !id) return `(no ${what})`;
  const target = catalog.resolve(id);
  return target ? catalog.label(target) : id.slice(0, 8) + '…';
}

function enumName(catalog: ContentCatalog, enumName: string, value: Json | undefined): string {
  const e = catalog.enums.get(enumName);
  return e?.values.find((v) => Number(v.value) === Number(value))?.displayName ?? e?.values.find((v) => Number(v.value) === Number(value))?.name ?? String(value ?? '?');
}

/** One node in words; children of logic nodes joined by and / or, nested groups in parentheses. */
export function conditionText(catalog: ContentCatalog, id: string | null | undefined, ctx: TextContext = {}, depth = 0): string {
  if (!id) return 'None';
  const node = nodeOf(catalog, id);
  if (!node) return '…';
  if (depth > MAX_DEPTH) return '…';
  const key = node.template.startsWith(CONDITION_NS) ? node.template.slice(CONDITION_NS.length) : node.template;
  const d = data(node);
  const zone = ctx.zone ?? 'utc';
  const children = childIds(node);
  const sub = (cid: string) => conditionText(catalog, cid, ctx, depth + 1);
  const group = (word: string, empty: string) => {
    const kids = ((d.conditions as Json[] | undefined) ?? []).filter((c): c is string => typeof c === 'string');
    if (kids.length === 0) return empty;
    const parts = kids.map((c) => {
      const t = sub(c);
      const cn = nodeOf(catalog, c);
      const nested = cn && (cn.template === `${CONDITION_NS}And` || cn.template === `${CONDITION_NS}Or`) && childIds(cn).length > 1;
      return nested ? `(${t})` : t;
    });
    return parts.join(` ${word} `);
  };
  switch (key) {
    case 'And':
      return group('and', 'always');
    case 'Or':
      return group('or', 'never');
    case 'Not':
      return children[0] ? `not (${sub(children[0])})` : 'not (nothing)';
    case 'Empty':
      return d.pass === false ? 'never (pass off)' : 'pass';
    case 'ProfileFieldNumber':
    case 'ProfileFieldLong':
    case 'ProfileFieldString':
      return Number(d.comparison) === 6 ? `${pathText(d.fullPath)} is empty` : `${pathText(d.fullPath)} ${comparisonText(d.comparison)} ${String(d.value ?? '')}`;
    case 'ProfileFieldBool':
      return `${pathText(d.fullPath)} is ${d.value ? 'true' : 'false'}`;
    case 'ProfileFieldInRange':
      return `${pathText(d.fullPath)} ∈ [${String(d.min ?? 0)}, ${String(d.max ?? 0)})`;
    case 'ProfileFieldList': {
      const check = CHECK_TYPES.find((c) => c.value === Number(d.checkType))?.label ?? 'Count';
      const filter = typeof d.filter === 'string' ? ` where ${sub(d.filter)}` : '';
      const inner = typeof d.condition === 'string' && check !== 'Count' ? ` matches ${sub(d.condition)}` : '';
      return check === 'Count' ? `count of ${pathText(d.fullPath)}${filter} ${comparisonText(d.comparison)} ${String(d.value ?? 0)}` : `${check.toLowerCase()} of ${pathText(d.fullPath)}${filter}${inner}`;
    }
    case 'TimeRage': {
      const start = typeof d.start === 'number' ? formatInstant(d.start, zone, { offsets: ctx.offsets }) : '?';
      const finish = typeof d.finish === 'number' ? formatInstant(d.finish, zone, { offsets: ctx.offsets }) : '?';
      const repeat = typeof d.repeat === 'number' && d.repeat > 0 ? `, every ${formatDuration(d.repeat)}` : '';
      return `Dates [${start}, ${finish}] ${zoneLabel(zone, { offsets: ctx.offsets })}${repeat}`;
    }
    case 'TimeOfTheDay':
      return `Time ${String(d.start ?? '00:00')} – ${String(d.finish ?? '00:00')}`;
    case 'DayOfTheWeek':
      return `Days: ${daysText(Number(d.days ?? 0))}`;
    case 'ActiveEvent':
      return `event ${ref(catalog, d.gameEvent, 'event')} is active`;
    case 'ActiveOffer':
      return `offer ${ref(catalog, d.gameOffer, 'offer')} is active`;
    case 'SegmentCondition':
      return `in segment ${ref(catalog, d.segment, 'segment')}`;
    case 'ABTestCondition':
      return `A/B test ${ref(catalog, d.test, 'test')}${typeof d.variant === 'string' ? ` variant ${ref(catalog, d.variant, 'variant')}` : ' (any variant)'}`;
    case 'Revenue':
      return `${REVENUE_KINDS.find((r) => r.value === Number(d.type))?.label ?? 'revenue'} ${comparisonText(d.comparison)} ${String(d.value ?? 0)} during the last ${String(d.days ?? 0)} days`;
    case 'StoreItemWasPurchased':
      return `purchased ${ref(catalog, d.storeItem, 'store item')} ${comparisonText(d.comparison)} ${String(d.value ?? 0)}`;
    case 'ProductWasPurchased': {
      const p = d.product as { productId?: string } | null | undefined;
      return `purchased product ${p?.productId ? (catalog.format.productLabel?.(p.productId) ?? p.productId) : '(none)'}`;
    }
    case 'InventoryItems':
      return `inventory ${ref(catalog, d.item, 'item')} ${comparisonText(d.comparison)} ${String(d.value ?? 0)}`;
    case 'AppVersion':
      return `app version ${comparisonText(d.comparison)} ${String(d.value ?? '')}`;
    case 'InstallVersion':
      return `install version ${comparisonText(d.comparison)} ${String(d.value ?? '')}`;
    case 'EngineVersion':
      return `engine version ${comparisonText(d.comparison)} ${String(d.value ?? '')}`;
    case 'Platform':
      return `platform ${enumName(catalog, 'SmartObjects.RuntimePlatform', d.value)}`;
    case 'BalancyPlatform':
      return `store platform ${enumName(catalog, 'SmartObjects.BalancyPlatform', d.value)}`;
    case 'Country':
      return `country ${String(d.code ?? '') || '(empty)'}`;
    case 'SystemLanguage':
      return `language ${ref(catalog, d.value, 'language')}`;
    default:
      return kindOf(node.template)?.label ?? catalog.label(node);
  }
}

export type TimeStatusKind = 'none' | 'in' | 'scheduled' | 'outside';

export interface TimeStatus {
  kind: TimeStatusKind;
  /** unix seconds of the next window start (scheduled / outside), when one exists */
  next?: number;
  /** unix seconds of the current window's end (in) */
  until?: number;
  /** the time leaves that fail now, in words */
  failing: string[];
}

interface LeafState {
  pass: boolean;
  next: number | null;
  until: number | null;
  text: string;
  started: boolean;
}

const DAY = 86400;

function leafState(catalog: ContentCatalog, node: DocumentLike, gameNow: number, ctx: TextContext): LeafState | null {
  const key = node.template.slice(CONDITION_NS.length);
  const d = data(node);
  const text = conditionText(catalog, node.id, ctx);
  switch (key) {
    case 'TimeRage': {
      const start = Number(d.start ?? 0);
      const finish = Number(d.finish ?? 0);
      const repeat = Number(d.repeat ?? 0);
      if (gameNow < start) return { pass: false, next: start, until: null, text, started: false };
      if (repeat > 0) {
        const cycle = Math.floor((gameNow - start) / repeat);
        const cs = start + cycle * repeat;
        const cf = finish + cycle * repeat;
        if (gameNow < cf) return { pass: true, next: null, until: cf, text, started: true };
        return { pass: false, next: cs + repeat, until: null, text, started: true };
      }
      if (gameNow < finish) return { pass: true, next: null, until: finish, text, started: true };
      return { pass: false, next: null, until: null, text, started: true };
    }
    case 'DayOfTheWeek': {
      const mask = Number(d.days ?? 0);
      const dayStart = Math.floor(gameNow / DAY) * DAY;
      const dow = new Date(gameNow * 1000).getUTCDay();
      if (mask & (1 << dow)) return { pass: true, next: null, until: dayStart + DAY, text, started: true };
      for (let i = 1; i <= 7; i++) {
        if (mask & (1 << ((dow + i) % 7))) return { pass: false, next: dayStart + i * DAY, until: null, text, started: true };
      }
      return { pass: false, next: null, until: null, text, started: true };
    }
    case 'TimeOfTheDay': {
      const s = minutesOf(d.start) ?? 0;
      const f = minutesOf(d.finish) ?? 0;
      const dayStart = Math.floor(gameNow / DAY) * DAY;
      const m = Math.floor((gameNow - dayStart) / 60);
      const pass = s <= f ? m >= s && m < f : m >= s || m < f;
      if (s === f) return { pass: false, next: null, until: null, text, started: true };
      if (pass) {
        const until = s <= f ? dayStart + f * 60 : m >= s ? dayStart + DAY + f * 60 : dayStart + f * 60;
        return { pass: true, next: null, until, text, started: true };
      }
      const next = m < s ? dayStart + s * 60 : dayStart + DAY + s * 60;
      return { pass: false, next, until: null, text, started: true };
    }
    default:
      return null;
  }
}

/** The schedule preview of a tree: its time leaves against game time now (And semantics; the other leaves are ignored). */
export function timeStatus(catalog: ContentCatalog, rootId: string | null | undefined, nowSeconds: number, ctx: TextContext = {}): TimeStatus {
  const zone = ctx.zone ?? 'utc';
  const offset = zone === 'env:dev' ? (ctx.offsets?.dev ?? 0) : zone === 'env:stage' ? (ctx.offsets?.stage ?? 0) : 0;
  const gameNow = nowSeconds + offset;
  const leaves: LeafState[] = [];
  const seen = new Set<string>();
  const walk = (id: string | null | undefined, depth: number) => {
    if (!id || seen.has(id) || depth > MAX_DEPTH) return;
    seen.add(id);
    const node = nodeOf(catalog, id);
    if (!node) return;
    const state = leafState(catalog, node, gameNow, ctx);
    if (state) leaves.push(state);
    for (const c of childIds(node)) walk(c, depth + 1);
  };
  walk(rootId, 0);
  if (leaves.length === 0) return { kind: 'none', failing: [] };
  const failing = leaves.filter((l) => !l.pass);
  const toServer = (t: number | null) => (t === null ? undefined : t - offset);
  if (failing.length === 0) {
    const until = Math.min(...leaves.map((l) => l.until ?? Number.POSITIVE_INFINITY));
    return { kind: 'in', until: Number.isFinite(until) ? toServer(until) : undefined, failing: [] };
  }
  const nexts = failing.map((l) => l.next).filter((n): n is number => n !== null);
  const next = nexts.length ? Math.max(...nexts) : null; // every failing leaf must start: the latest of their next starts
  const neverStarted = failing.some((l) => !l.started);
  return { kind: neverStarted ? 'scheduled' : 'outside', next: next === null ? undefined : toServer(next), failing: failing.map((l) => l.text) };
}

export function timeStatusText(status: TimeStatus, zone: DisplayZone, offsets?: { dev?: number; stage?: number }): string {
  switch (status.kind) {
    case 'none':
      return 'no time component';
    case 'in':
      return status.until !== undefined ? `in window until ${formatInstant(status.until, zone, { offsets })}` : 'in window';
    case 'scheduled':
      return status.next !== undefined ? `scheduled; next: ${formatInstant(status.next, zone, { offsets })}` : 'scheduled';
    case 'outside':
      return status.next !== undefined ? `outside window; next: ${formatInstant(status.next, zone, { offsets })}` : 'over';
  }
}
